import type { GameState, CountryCode, RandomEvent } from './types';
import { COUNTRIES, INITIAL_BUILDINGS, INITIAL_UPGRADES, ACHIEVEMENTS, RANDOM_EVENTS, FUN_FACTS } from './data';
import confetti from 'canvas-confetti';

const SAVE_KEY = 'chicken-empire-save';
const TICK_RATE = 50; // ms
const EVENT_CHECK_INTERVAL = 10000; // Check for events every 10 seconds

export class ChickenEmpireGame {
  state: GameState;
  tickInterval: number | null = null;
  eventInterval: number | null = null;
  onUpdate: (state: GameState) => void;
  onAchievement: (achievement: string) => void;
  onEvent: (event: RandomEvent) => void;
  onFact: (fact: string) => void;

  constructor(
    onUpdate: (state: GameState) => void,
    onAchievement: (achievement: string) => void,
    onEvent: (event: RandomEvent) => void,
    onFact: (fact: string) => void
  ) {
    this.onUpdate = onUpdate;
    this.onAchievement = onAchievement;
    this.onEvent = onEvent;
    this.onFact = onFact;
    this.state = this.loadGame() || this.createInitialState();
  }

  createInitialState(): GameState {
    return {
      chickens: 0,
      totalChickensProduced: 0,
      money: 0,
      totalMoneyEarned: 0,
      country: 'sa',
      selfSufficiency: 0,
      chickensPerSecond: 0,
      chickenPrice: 5,
      buildings: JSON.parse(JSON.stringify(INITIAL_BUILDINGS)),
      upgrades: JSON.parse(JSON.stringify(INITIAL_UPGRADES)),
      achievements: JSON.parse(JSON.stringify(ACHIEVEMENTS)),
      activeEvents: [],
      prestigeLevel: 0,
      exportLicenses: 0,
      totalClicks: 0,
      playTime: 0,
      lastSave: Date.now(),
      lastUpdate: Date.now(),
      phase: 'title',
      unlockedCountries: ['sa'],
    };
  }

  startGame(country: CountryCode) {
    const countryData = COUNTRIES[country];
    this.state.country = country;
    this.state.selfSufficiency = countryData.selfSufficiency;
    this.state.phase = 'playing';
    this.state.lastUpdate = Date.now();
    this.startTicking();
    this.saveGame();
  }

  startTicking() {
    if (this.tickInterval) return;

    this.tickInterval = window.setInterval(() => this.tick(), TICK_RATE);
    this.eventInterval = window.setInterval(() => this.checkForEvents(), EVENT_CHECK_INTERVAL);

    // Show random fact every 30-60 seconds
    this.scheduleFact();
  }

  scheduleFact() {
    const delay = 30000 + Math.random() * 30000;
    setTimeout(() => {
      if (this.state.phase === 'playing') {
        const fact = FUN_FACTS[Math.floor(Math.random() * FUN_FACTS.length)];
        this.onFact(fact.text);
        this.scheduleFact();
      }
    }, delay);
  }

  stopTicking() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    if (this.eventInterval) {
      clearInterval(this.eventInterval);
      this.eventInterval = null;
    }
  }

  tick() {
    const now = Date.now();
    const delta = (now - this.state.lastUpdate) / 1000;
    this.state.lastUpdate = now;
    this.state.playTime += delta;

    // Calculate production
    const production = this.calculateProduction() * delta;
    this.state.chickens += production;
    this.state.totalChickensProduced += production;

    // Auto-sell if automation upgrade
    const autoLevel = this.state.upgrades.automation?.level || 0;
    if (autoLevel > 0) {
      const autoSellPercent = autoLevel * 0.1;
      const toSell = this.state.chickens * autoSellPercent * delta;
      if (toSell > 0) {
        this.sellChickens(toSell, true);
      }
    }

    // Update self-sufficiency based on total production
    this.updateSelfSufficiency();

    // Check active events
    this.state.activeEvents = this.state.activeEvents.filter(e => e.endTime > now);

    // Check achievements
    this.checkAchievements();

    // Auto-save every 30 seconds
    if (now - this.state.lastSave > 30000) {
      this.saveGame();
    }

    this.onUpdate(this.state);
  }

  calculateProduction(): number {
    let base = 0;

    for (const building of Object.values(this.state.buildings)) {
      base += building.baseProduction * building.count;
    }

    // Apply production multipliers
    let multiplier = 1;

    // Upgrades
    for (const upgrade of Object.values(this.state.upgrades)) {
      if (upgrade.effect === 'production' && upgrade.level > 0) {
        multiplier *= (1 + upgrade.effectValue * upgrade.level);
      }
    }

    // Country bonuses
    const country = COUNTRIES[this.state.country];
    if (country.code === 'ae') {
      // Hand slaughter penalty
      multiplier *= 0.85;
    }

    // Active events
    for (const active of this.state.activeEvents) {
      if (active.event.effect === 'production') {
        multiplier *= active.event.effectValue;
      }
    }

    // Prestige bonus
    multiplier *= (1 + this.state.prestigeLevel * 0.1);

    this.state.chickensPerSecond = base * multiplier;
    return base * multiplier;
  }

  calculatePrice(): number {
    let price = this.state.chickenPrice;

    // Upgrades
    for (const upgrade of Object.values(this.state.upgrades)) {
      if (upgrade.effect === 'price' && upgrade.level > 0) {
        price *= (1 + upgrade.effectValue * upgrade.level);
      }
    }

    // Country bonuses
    const country = COUNTRIES[this.state.country];
    if (country.code === 'ae') {
      // Premium market
      price *= 1.2;
    } else if (country.code === 'bh') {
      // Premium focus
      price *= 1.15;
    }

    // Active events
    for (const active of this.state.activeEvents) {
      if (active.event.effect === 'price') {
        price *= active.event.effectValue;
      }
    }

    return price;
  }

  calculateBuildingCost(buildingId: string): number {
    const building = this.state.buildings[buildingId];
    if (!building) return Infinity;

    let cost = building.baseCost * Math.pow(building.costMultiplier, building.count);

    // Apply cost reductions
    const country = COUNTRIES[this.state.country];
    cost *= (1 - country.feedSubsidy / 100);

    // Government lobby upgrade
    const lobbyLevel = this.state.upgrades.governmentLobby?.level || 0;
    if (lobbyLevel > 0) {
      cost *= Math.pow(0.7, lobbyLevel);
    }

    // Active events
    for (const active of this.state.activeEvents) {
      if (active.event.effect === 'cost') {
        cost *= active.event.effectValue;
      }
    }

    return Math.floor(cost);
  }

  calculateUpgradeCost(upgradeId: string): number {
    const upgrade = this.state.upgrades[upgradeId];
    if (!upgrade) return Infinity;
    return Math.floor(upgrade.baseCost * Math.pow(upgrade.costMultiplier, upgrade.level));
  }

  clickChicken() {
    this.state.totalClicks++;
    const clickValue = 1 + Math.floor(this.state.totalChickensProduced / 1000);
    this.state.chickens += clickValue;
    this.state.totalChickensProduced += clickValue;
    this.updateSelfSufficiency();
    this.checkAchievements();
    this.onUpdate(this.state);
  }

  sellChickens(amount?: number, silent = false) {
    const toSell = amount || this.state.chickens;
    if (toSell <= 0) return;

    const actualSell = Math.min(toSell, this.state.chickens);
    const price = this.calculatePrice();
    const revenue = actualSell * price;

    this.state.chickens -= actualSell;
    this.state.money += revenue;
    this.state.totalMoneyEarned += revenue;

    if (!silent) {
      this.onUpdate(this.state);
    }
  }

  buyBuilding(buildingId: string) {
    const cost = this.calculateBuildingCost(buildingId);
    if (this.state.money < cost) return false;

    this.state.money -= cost;
    this.state.buildings[buildingId].count++;
    this.onUpdate(this.state);
    return true;
  }

  buyUpgrade(upgradeId: string) {
    const upgrade = this.state.upgrades[upgradeId];
    if (!upgrade || upgrade.level >= upgrade.maxLevel) return false;

    const cost = this.calculateUpgradeCost(upgradeId);
    if (this.state.money < cost) return false;

    this.state.money -= cost;
    this.state.upgrades[upgradeId].level++;
    this.onUpdate(this.state);
    return true;
  }

  updateSelfSufficiency() {
    const country = COUNTRIES[this.state.country];
    // Self-sufficiency grows based on total production relative to demand
    const annualDemand = country.perCapitaConsumption * country.population * 1000000;
    const progress = (this.state.totalChickensProduced / annualDemand) * 100;

    this.state.selfSufficiency = Math.min(
      100,
      country.selfSufficiency + progress * 0.001
    );
  }

  checkForEvents() {
    if (this.state.phase !== 'playing') return;

    const now = Date.now();

    // Filter events for current country
    const availableEvents = RANDOM_EVENTS.filter(e =>
      !e.country || e.country === this.state.country
    );

    for (const event of availableEvents) {
      // Skip if already active
      if (this.state.activeEvents.some(a => a.event.id === event.id)) continue;

      if (Math.random() < event.probability) {
        this.state.activeEvents.push({
          event,
          endTime: now + event.duration * 1000,
        });
        this.onEvent(event);
        break; // Only one event at a time
      }
    }
  }

  checkAchievements() {
    for (const achievement of Object.values(this.state.achievements)) {
      if (achievement.unlocked) continue;

      let achieved = false;
      switch (achievement.type) {
        case 'chickens':
          achieved = this.state.totalChickensProduced >= achievement.requirement;
          break;
        case 'money':
          achieved = this.state.totalMoneyEarned >= achievement.requirement;
          break;
        case 'selfSufficiency':
          achieved = this.state.selfSufficiency >= achievement.requirement;
          break;
        case 'buildings':
          const total = Object.values(this.state.buildings).reduce((sum, b) => sum + b.count, 0);
          achieved = total >= achievement.requirement;
          break;
      }

      if (achieved) {
        this.state.achievements[achievement.id].unlocked = true;
        this.onAchievement(achievement.id);

        // Celebration!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });

        // Check for special rewards
        if (achievement.id === 'vision2030') {
          // Unlock export terminal early
          this.state.buildings.exportTerminal.unlockAt = 0;
        } else if (achievement.id === 'fullyIndependent') {
          // Unlock prestige mode
          this.state.phase = 'prestige';
        }
      }
    }
  }

  prestige() {
    // Calculate export licenses earned
    const licenses = Math.floor(Math.log10(this.state.totalChickensProduced + 1));
    this.state.exportLicenses += licenses;
    this.state.prestigeLevel++;

    // Unlock new country based on prestige level
    const countryOrder: CountryCode[] = ['sa', 'kw', 'ae', 'om', 'qa', 'bh'];
    if (this.state.prestigeLevel < countryOrder.length) {
      const newCountry = countryOrder[this.state.prestigeLevel];
      if (!this.state.unlockedCountries.includes(newCountry)) {
        this.state.unlockedCountries.push(newCountry);
      }
    }

    // Reset progress but keep prestige bonuses
    this.state.chickens = 0;
    this.state.totalChickensProduced = 0;
    this.state.money = 0;
    this.state.totalMoneyEarned = 0;
    this.state.selfSufficiency = 0;
    this.state.buildings = JSON.parse(JSON.stringify(INITIAL_BUILDINGS));
    this.state.upgrades = JSON.parse(JSON.stringify(INITIAL_UPGRADES));
    this.state.activeEvents = [];
    this.state.phase = 'country-select';

    this.saveGame();
    this.onUpdate(this.state);

    // Big celebration
    confetti({
      particleCount: 200,
      spread: 120,
      origin: { y: 0.4 }
    });
  }

  saveGame() {
    this.state.lastSave = Date.now();
    localStorage.setItem(SAVE_KEY, JSON.stringify(this.state));
  }

  loadGame(): GameState | null {
    const saved = localStorage.getItem(SAVE_KEY);
    if (!saved) return null;

    try {
      const state = JSON.parse(saved) as GameState;

      // Calculate offline progress
      if (state.phase === 'playing') {
        const offlineTime = (Date.now() - state.lastUpdate) / 1000;
        const offlineProduction = state.chickensPerSecond * offlineTime * 0.5; // 50% offline efficiency
        state.chickens += offlineProduction;
        state.totalChickensProduced += offlineProduction;
      }

      return state;
    } catch {
      return null;
    }
  }

  resetGame() {
    localStorage.removeItem(SAVE_KEY);
    this.state = this.createInitialState();
    this.onUpdate(this.state);
  }

  formatNumber(num: number): string {
    if (num < 1000) return Math.floor(num).toLocaleString();

    const suffixes = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc'];
    const tier = Math.floor(Math.log10(num) / 3);

    if (tier >= suffixes.length) {
      return num.toExponential(2);
    }

    const scaled = num / Math.pow(1000, tier);
    return scaled.toFixed(scaled < 10 ? 2 : scaled < 100 ? 1 : 0) + suffixes[tier];
  }
}
